import { Api, AxiosConfig } from '@coder-house/ch-coder-util'
import { IOnboarding } from '../models/user.interface'
import { getUser } from './user'
import { getUrlParams } from '../utils/utils'

const { VUE_APP_CH_API_BFF } = process.env

function updateTokensInStorage(idtoken: string, accesstoken: string) {
  localStorage.setItem('accesstoken', accesstoken)
  localStorage.setItem('idtoken', idtoken)
}

async function login(idtoken: string, accesstoken: string): Promise<void> {
  updateTokensInStorage(idtoken, accesstoken)
  await isAuthenticated()
}

// Función helper para limpiar cookies
function clearCookie(name: string) {
  document.cookie = `${name}=; max-age=0; path=/; SameSite=None; Secure; domain=.coderhouse.com`
}

function clearTokens(keys: Array<string>): void {
  AxiosConfig.removeHeaders(keys)
  for (const key of keys) {
    window.localStorage.removeItem(key)
    // También limpiar las cookies
    clearCookie(key)
  }
}

// Función helper para configurar cookies
function setCookie(name: string, value: string, days: number = 7) {
  const maxAge = days * 24 * 60 * 60 // segundos
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=None; Secure; domain=.coderhouse.com`
}

async function isAuthenticated(): Promise<boolean> {
  const token = window.localStorage.getItem('idtoken')
  const auth = window.localStorage.getItem('accesstoken')
  
  if (token && auth) {
    // Configurar headers como antes
    AxiosConfig.addHeader('idtoken', token)
    AxiosConfig.addHeader('accesstoken', auth)
    
    // Configurar cookies también
    setCookie('idtoken', token)
    setCookie('accesstoken', auth)
  }
  
  AxiosConfig.setWithCredentials(true)

  return !!token && !!auth
}

async function hasOnboardingCompleted(userId: string): Promise<IOnboarding> {
  const storedOnboarding = window.localStorage.getItem('onboarding')
  let hasOnboarding = null

  if (!!storedOnboarding) {
    hasOnboarding = JSON.parse(storedOnboarding)
  } else {
    hasOnboarding = await Api.get<IOnboarding>(`${VUE_APP_CH_API_BFF}/v1/home/onboarding-complete/${userId}`)
    window.localStorage.setItem('onboarding', JSON.stringify(hasOnboarding))
  }

  return hasOnboarding
}

const manageSession = async () => {
  const { idtoken, accesstoken } = getUrlParams(window.location.search)
  overwriteTokens({ idtoken, accesstoken })

  const auth = await isAuthenticated()
  if (!auth && idtoken && accesstoken) {
    await login(idtoken, accesstoken)
  }

  // await verifyToken({ idtoken: window.localStorage.getItem('idtoken') })
  await getUser()
}

function overwriteTokens({ idtoken, accesstoken }) {
  if (idtoken && accesstoken) {
    clearTokens(['idtoken', 'accesstoken', 'user'])
    updateTokensInStorage(idtoken, accesstoken)
  }
}

async function verifyToken({ idtoken }) {
  const res = await fetch(`${process.env.VITE_CH_AUTH_SERVICE}/api/v1/auth/verify_token`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      idtoken: idtoken
    },
    credentials: 'include' // Equivalent to withCredentials: true in Axios
  })
  const data = await res.json()
  return data
}

function overwriteTokensIfImpersonation({ idtoken, accesstoken, impersonate }) {
  if (!impersonate) return

  if (idtoken && accesstoken) {
    clearTokens(['idtoken', 'accesstoken', 'user'])
    updateTokensInStorage(idtoken, accesstoken)
  }
}

// Función helper para obtener cookies
function getCookie(name: string): string | null {
  const nameEQ = name + "="
  const cookies = String(document.cookie).split(';')
  for (let i = 0; i < cookies.length; i++) {
    let c = cookies[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

export { login, clearTokens, isAuthenticated, hasOnboardingCompleted, manageSession, setCookie, getCookie, clearCookie }